import Auth from "../../Auth";

export async function getBigDocumentSnapshotContent(documentId: string, contentUuid: string) {
  return await Auth.fetchWithIdToken("/api/document/snapshots/get-big-document-snapshot-content", {
    method: "POST",
    documentId,
    contentUuid,
  });
}

export async function createDocumentSnapshot(documentId: string, snapshotContent: string) {
  return await Auth.fetchWithIdToken("/api/document/snapshots/create-document-snapshot", {
    method: "POST",
    documentId,
    snapshotContent,
  });
}

interface PushClientSnapshotParams {
  documentId: string;
  // Tiptap-shaped JSON produced by TiptapTransformer.fromYdoc.
  snapshotContent: unknown;
  // Pre-fetched Firebase id token. The caller is responsible for refreshing
  // this on a cadence — getIdToken() can't be awaited reliably inside
  // beforeunload / visibilitychange handlers (the page may be tearing down
  // before the token resolves), so the snapshot push uses a cached value.
  idToken: string;
  // Trigger label for logs/Sentry breadcrumbs: e.g. "visibility-hidden",
  // "beforeunload", "destroy", "manual".
  reason: string;
  // Set true on beforeunload so the request survives page unload (browsers
  // honor keepalive specifically for this case). Capped at ~64KB by spec;
  // large docs should NOT set this. Default false.
  keepalive?: boolean;
}

/**
 * Pushes a client-side document snapshot over plain HTTP.
 *
 * This is the safety net for stale-tab and offline-editing failure modes.
 * Current editor reconnect/auth-failure handling intentionally keeps the editor
 * editable while IndexedDB preserves local Yjs changes; the saved indicator
 * moves to "Saved Locally" when we know the provider is offline. If WS sync is
 * disconnected or stale (background tab idle disconnect, Firebase token expiry
 * mid-session, network change, etc.), content can accumulate locally before it
 * reaches the server. Periodic + visibility/unload triggers in Editor.js call
 * this function to deliver snapshots independently of the WS.
 *
 * The documentation change from text identity to manuscript identity refers to the server's
 * dedup key, not a new client snapshot format. ProseMirror JSON already includes annotation
 * marks; the server must not discard a snapshot just because Feedback changed marks without
 * changing the prose. Existing snapshots that never saved marks cannot recover them
 * retroactively.
 *
 * The server endpoint (processSynchronousSnapshot) already dedups by manuscript
 * identity, so this is safe to call liberally — duplicate content is rejected
 * server-side without writing a new snapshot row.
 *
 * Fire-and-forget: callers should not await. Failures are swallowed so editor
 * UX is never blocked. Use `keepalive: true` only on beforeunload (browsers
 * cap keepalive payloads at ~64KB).
 */
export async function pushClientSnapshot(
  params: PushClientSnapshotParams,
): Promise<{ ok: boolean; skipped?: boolean; snapshotId?: string; status?: number } | void> {
  const { documentId, snapshotContent, idToken, keepalive = false } = params;
  // `reason` is preserved on the client side (Editor.js tracks
  // `_lastSnapshotPushReason` for sw.snapshot.status()), but we deliberately
  // do NOT send it in the request body — the server-side Zod schema strips
  // unknown keys and the snapshot record schema has no field for it. Sending
  // dead data would just add weight to every push.
  const body = JSON.stringify({
    documentId,
    snapshotContent,
    idToken,
  });
  try {
    const res = await fetch("/api/document/snapshots/create-document-snapshot", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        // recordClientVersion middleware on /api/document/* logs a warn-level
        // "unknown client version" line whenever this header is missing.
        // Match what Auth.fetchWithIdToken would have sent.
        "x-sudowrite-client-version": (typeof window !== "undefined" && (window as any).sudowriteClientVersion) || "loading",
      },
      credentials: "include",
      keepalive,
      body,
    });
    let payload: any = null;
    try {
      payload = await res.clone().json();
    } catch (_e) {
      // non-JSON or empty body — leave payload null
    }
    return {
      ok: res.ok,
      status: res.status,
      skipped: Boolean(payload?.skipped),
      snapshotId: payload?.snapshotId ?? undefined,
    };
  } catch (_err) {
    // best-effort; never block the editor on snapshot push failures.
    // Failures here cover both synchronous fetch errors (e.g., body too large
    // for keepalive) and rejected fetch promises (network blips). The next
    // visibility-hidden / beforeunload / destroy trigger will retry; Editor.js
    // wraps the encode step with Sentry capture for non-routine failures.
    return;
  }
}
